import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAccommodationStore } from '../stores/accommodationStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import { calculateAge } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import { ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

type LocationState = {
  fileData: string;
  tableId: string;
};

type BulkUploadRow = {
  roomNumber: string;
  bedIndex: number;
  passportNumber: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  message?: string;
};

export const BulkAccommodationStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tables } = useAccommodationStore();
  const { translations } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rows, setRows] = useState<BulkUploadRow[]>([]);
  const [stats, setStats] = useState({ total: 0, processed: 0, success: 0, error: 0 });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const state = location.state as LocationState | undefined;

  // Process the uploaded file data
  useEffect(() => {
    if (!state?.fileData || !state?.tableId) {
      setErrorMessage('No file data or table ID provided');
      setLoading(false);
      return;
    }

    try {
      // Parse the Excel file
      const workbook = XLSX.read(state.fileData, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(worksheet);

      // Normalize and validate the data
      const uploadRows: BulkUploadRow[] = data.map((row: any) => {
        // Extract and normalize fields
        const roomNumber = String(row['Room Number'] || row['room number'] || row['roomNumber'] || '');
        const bedIndex = parseInt(String(row['Bed Index'] || row['bed index'] || row['bedIndex'] || '0')) - 1; // Convert to 0-based index
        const passportNumber = normalizePassportNumber(row['Passport Number'] || row['passport number'] || row['passportNumber'] || '');

        // Validate fields
        const errors = [];
        if (!roomNumber) errors.push('Room number is required');
        if (isNaN(bedIndex) || bedIndex < 0) errors.push('Invalid bed index');
        if (!passportNumber) errors.push('Passport number is required');

        return {
          roomNumber,
          bedIndex,
          passportNumber,
          status: errors.length > 0 ? 'error' : 'pending',
          error: errors.length > 0 ? errors.join(', ') : undefined,
        };
      });

      setRows(uploadRows);
      setStats({
        total: uploadRows.length,
        processed: 0,
        success: 0,
        error: uploadRows.filter(r => r.status === 'error').length,
      });
      setLoading(false);
    } catch (error: any) {
      console.error('Error processing file:', error);
      setErrorMessage(error.message || 'Failed to process the file');
      setLoading(false);
    }
  }, [state]);

  // Process the bulk accommodation
  const processAccommodation = async () => {
    if (!state?.tableId || rows.length === 0) return;

    setProcessing(true);
    let processed = 0;
    let success = 0;
    let errors = 0;
    const updatedRows = [...rows];
    const table = tables[state.tableId];
    
    if (!table) {
      setErrorMessage('Table not found');
      setProcessing(false);
      return;
    }

    // Process in smaller batches
    const batchSize = 20;
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      await processRowBatch(batch, i, updatedRows);
      
      // Update stats after each batch
      processed += batch.length;
      success = updatedRows.filter(r => r.status === 'success').length;
      errors = updatedRows.filter(r => r.status === 'error').length;
      
      setStats({ total: rows.length, processed, success, errors });
      setRows([...updatedRows]);
    }

    setProcessing(false);
    toast.success(`Processed ${success} of ${rows.length} records successfully`);
  };

  // Process a batch of rows
  const processRowBatch = async (batch: BulkUploadRow[], startIndex: number, allRows: BulkUploadRow[]) => {
    const table = tables[state!.tableId];
    
    for (let i = 0; i < batch.length; i++) {
      const index = startIndex + i;
      const row = batch[i];
      
      // Skip rows that already have an error
      if (row.status === 'error') continue;

      try {
        // Find the room in the table - treat room number as a string for comparison
        const foundRoom = table.r.find(r => r.n === row.roomNumber);
        
        if (!foundRoom) {
          throw new Error(`Room ${row.roomNumber} not found`);
        }
        
        // Validate bed index
        if (row.bedIndex < 0 || row.bedIndex >= foundRoom.b.length) {
          throw new Error(`Bed index ${row.bedIndex + 1} is out of range for room ${row.roomNumber}`);
        }
        
        // Check if bed is already occupied
        if (foundRoom.b[row.bedIndex].a && foundRoom.b[row.bedIndex].a.length > 0) {
          throw new Error(`Bed ${row.bedIndex + 1} in room ${row.roomNumber} is already occupied`);
        }

        // Find pilgrim by passport number
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        const q = query(
          pilgrimsRef,
          where('Passport_Number', '==', row.passportNumber)
        );
        const pilgrimSnapshot = await getDocs(q);
        
        if (pilgrimSnapshot.empty) {
          throw new Error(`Pilgrim with passport ${row.passportNumber} not found`);
        }
        
        const pilgrimDoc = pilgrimSnapshot.docs[0];
        const pilgrimData = pilgrimDoc.data();
        
        // Find guide link
        const linksRef = collection(firestore, 'PilgrimGuideLinks');
        const linksQuery = query(
          linksRef,
          where('pilgrimId', '==', pilgrimDoc.id),
          where('status', '==', 'approved')
        );
        const linksSnapshot = await getDocs(linksQuery);
        
        if (linksSnapshot.empty) {
          throw new Error(`No approved guide link found for pilgrim with passport ${row.passportNumber}`);
        }
        
        const linkData = linksSnapshot.docs[0].data();
        
        // Get guide info
        const guidesRef = collection(firestore, 'Guides');
        const guideQuery = query(
          guidesRef,
          where('__name__', '==', linkData.guideId)
        );
        const guideSnapshot = await getDocs(guideQuery);
        
        if (guideSnapshot.empty) {
          throw new Error(`Guide not found for pilgrim with passport ${row.passportNumber}`);
        }
        
        const guideData = guideSnapshot.docs[0].data();
        
        // Get organizer info
        const organizersRef = collection(firestore, 'Organizers');
        const organizerQuery = query(
          organizersRef,
          where('__name__', '==', guideData.organizerId)
        );
        const organizerSnapshot = await getDocs(organizerQuery);
        
        if (organizerSnapshot.empty) {
          throw new Error(`Organizer not found for guide of pilgrim with passport ${row.passportNumber}`);
        }
        
        const organizerData = organizerSnapshot.docs[0].data();
        
        // Get stay dates - check PilgrimSelector first, then package
        const selectorRef = collection(firestore, 'PilgrimSelector');
        const selectorQuery = query(
          selectorRef,
          where('pilgrimId', '==', pilgrimDoc.id)
        );
        const selectorSnapshot = await getDocs(selectorQuery);
        
        let checkInDate: string | undefined;
        let checkOutDate: string | undefined;
        
        if (!selectorSnapshot.empty) {
          const selectorData = selectorSnapshot.docs[0].data();
          const packageData = selectorData.packageData;
          
          // Try to get dates for this specific hotel
          if (packageData.mainMakkah && packageData.mainMakkah.hotel === table.hn) {
            checkInDate = packageData.mainMakkah.startDate;
            checkOutDate = packageData.mainMakkah.endDate;
          } else if (
            packageData.shiftingMakkah && 
            packageData.shiftingMakkah.hotel === table.hn
          ) {
            checkInDate = packageData.shiftingMakkah.startDate;
            checkOutDate = packageData.shiftingMakkah.endDate;
          } else if (packageData.madinah && packageData.madinah.hotel === table.hn) {
            checkInDate = packageData.madinah.startDate;
            checkOutDate = packageData.madinah.endDate;
          }
          
          if (!checkInDate || !checkOutDate) {
            throw new Error(`No stay dates found for pilgrim with passport ${row.passportNumber} at hotel ${table.hn}`);
          }
        } else {
          throw new Error(`No selector data found for pilgrim with passport ${row.passportNumber}`);
        }
        
        // Create the assignment
        const assignment = {
          pid: pilgrimDoc.id,
          pn: pilgrimData.Name,
          ci: checkInDate,
          co: checkOutDate,
          g: pilgrimData.Gender === 'male' ? 'M' : 'F',
          gid: linkData.guideId,
          gn: guideData.name,
          oid: guideData.organizerId,
          on: organizerData.name,
          age: calculateAge(pilgrimData.Date_of_Birth)
        };
        
        // Update firestore
        const batch = writeBatch(firestore);
        
        // 1. Update the accommodation table
        const tableRef = doc(firestore, 'AccommodationTables', state!.tableId);
        const tableData = { ...table };
        
        // Find the room and update it
        const roomIndex = tableData.r.findIndex(r => r.n === row.roomNumber);
        if (roomIndex === -1) {
          throw new Error(`Room ${row.roomNumber} not found`);
        }
        
        // Use a different variable name to avoid redeclaration
        const targetRoom = { ...tableData.r[roomIndex] };
        const beds = [...targetRoom.b];
        
        // Add assignment to the bed
        if (!beds[row.bedIndex].a) {
          beds[row.bedIndex].a = [];
        }
        
        beds[row.bedIndex].a.push(assignment);
        
        // Update room occupancy
        targetRoom.o = targetRoom.o + 1;
        targetRoom.b = beds;
        
        // Update the rooms array
        tableData.r[roomIndex] = targetRoom;
        
        // Update the table
        batch.update(tableRef, {
          r: tableData.r,
          ua: Date.now()
        });
        
        // 2. Update PilgrimSelector
        const selector = selectorSnapshot.docs[0];
        const selectorData = selector.data();
        
        // Update isAccommodated property
        const updatedAccommodation = { ...selectorData.isAccommodated } || {};
        updatedAccommodation[table.hn] = true;
        
        batch.update(selector.ref, {
          isAccommodated: updatedAccommodation,
          updatedAt: Date.now()
        });
        
        // Commit the batch
        await batch.commit();
        
        // Update row status
        allRows[index] = {
          ...row,
          status: 'success',
          message: `Successfully accommodated in room ${row.roomNumber}, bed ${row.bedIndex + 1}`
        };
        
      } catch (error: any) {
        // Update row status with error
        allRows[index] = {
          ...row,
          status: 'error',
          error: error.message || 'Unknown error'
        };
      }
    }
  };

  // Export results as Excel file
  const exportResults = () => {
    const exportData = rows.map(row => ({
      'Room Number': row.roomNumber,
      'Bed Index': row.bedIndex + 1,
      'Passport Number': row.passportNumber,
      'Status': row.status === 'success' ? 'Success' : 'Error',
      'Message': row.status === 'success' ? row.message : row.error
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Results');
    XLSX.writeFile(wb, `accommodation-results-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) return <LoadingSpinner />;

  if (errorMessage) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 p-6 rounded-lg text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">{translations?.bulkAccommodation?.error || 'Error'}</h2>
          <p className="text-red-600">{errorMessage}</p>
          <button 
            onClick={() => navigate('/accommodation')}
            className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            <ChevronRight className="ml-2" size={16} />
            <span>{translations?.bulkAccommodation?.backToAccommodation || 'Back to Accommodation'}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">
        {translations?.bulkAccommodation?.statusTitle || 'Bulk Accommodation Status'}
      </h1>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              {translations?.bulkAccommodation?.totalRecords || 'Total Records'}
            </h3>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              {translations?.bulkAccommodation?.processed || 'Processed'}
            </h3>
            <p className="text-2xl font-bold">{stats.processed}</p>
            <div className="w-full bg-gray-200 h-1.5 mt-2 rounded-full overflow-hidden">
              <div 
                className="bg-primary-500 h-full" 
                style={{ width: `${stats.total > 0 ? (stats.processed / stats.total) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600 mb-1">
              {translations?.bulkAccommodation?.success || 'Success'}
            </h3>
            <p className="text-2xl font-bold text-green-700">{stats.success}</p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-red-600 mb-1">
              {translations?.bulkAccommodation?.errors || 'Errors'}
            </h3>
            <p className="text-2xl font-bold text-red-700">{stats.error}</p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-between mt-6">
          <button
            onClick={() => navigate('/accommodation')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {translations?.bulkAccommodation?.backToAccommodation || 'Back to Accommodation'}
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={exportResults}
              disabled={rows.length === 0}
              className="px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {translations?.bulkAccommodation?.exportResults || 'Export Results'}
            </button>
            
            <button
              onClick={processAccommodation}
              disabled={processing || rows.length === 0 || rows.every(r => r.status !== 'pending')}
              className={`px-6 py-2 rounded-lg text-white transition-colors ${
                processing || rows.length === 0 || rows.every(r => r.status !== 'pending')
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {processing ? (
                <span>{translations?.bulkAccommodation?.processing || 'Processing...'}</span>
              ) : (
                <span>{translations?.bulkAccommodation?.process || 'Process Accommodation'}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.bulkAccommodation?.tableHeaders?.roomNumber || 'Room Number'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.bulkAccommodation?.tableHeaders?.bedIndex || 'Bed Index'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.bulkAccommodation?.tableHeaders?.passport || 'Passport Number'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.bulkAccommodation?.tableHeaders?.status || 'Status'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.bulkAccommodation?.tableHeaders?.message || 'Message'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row, index) => (
                <motion.tr 
                  key={index}
                  initial={processing ? { opacity: 0.5 } : { opacity: 1 }}
                  animate={{ 
                    opacity: 1,
                    backgroundColor: row.status === 'success' ? 'rgba(243, 244, 246, 0.2)' : 
                                    row.status === 'error' ? 'rgba(254, 226, 226, 0.2)' :
                                    'rgba(255, 255, 255, 1)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.roomNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.bedIndex + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.passportNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {row.status === 'success' && (
                        <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="mr-1" size={12} />
                          {translations?.bulkAccommodation?.success || 'Success'}
                        </span>
                      )}
                      {row.status === 'error' && (
                        <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="mr-1" size={12} />
                          {translations?.bulkAccommodation?.error || 'Error'}
                        </span>
                      )}
                      {row.status === 'pending' && (
                        <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {translations?.bulkAccommodation?.pending || 'Pending'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.status === 'success' ? row.message : row.error}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BulkAccommodationStatusPage;
